import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTheme } from "../context/ThemeContext";

const Typearticles = () => {
  const { typearticles = [] } = useSelector(state => state.home);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [visibleItems, setVisibleItems] = useState(5);

  const [isTablet, setIsTablet] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const containerRef = useRef(null);

  const filteredTypearticles = (typearticles || []).filter(c => c.verpublish !== false && c.image && c.image.trim() !== "");
  const { getBackground } = useTheme();

  // Actualizar responsive según el tamaño de pantalla
  useEffect(() => {
    const updateResponsive = () => {
      const width = window.innerWidth;

      // Mobile: < 768px
      if (width < 768) {
        setIsMobile(true);
        setIsTablet(false);
     
        setVisibleItems(1);
      }
      // Tablet: 768px - 1023px
      else if (width >= 768 && width < 1024) {
        setIsMobile(false);
        setIsTablet(true);
     
        setVisibleItems(3);
      }
      // Desktop: >= 1024px
      else {
        setIsMobile(false);
        setIsTablet(false);
        setVisibleItems(4);
      }
    };

    updateResponsive();
    window.addEventListener("resize", updateResponsive);

    return () => {
      window.removeEventListener("resize", updateResponsive);
    };
  }, []);

  // Configurar el carrusel basado en el número de imágenes visibles
  const totalItems = filteredTypearticles.length;
  const maxIndex = Math.max(0, totalItems - visibleItems);

  // Navegación con flechas
  const nextSlide = () => {
    if (currentIndex >= maxIndex) return;

    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex(prev => Math.min(prev + 1, maxIndex));
      setTimeout(() => {
        setIsTransitioning(false);
      }, 300);
    }, 50);
  };

  const prevSlide = () => {
    if (currentIndex <= 0) return;

    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex(prev => Math.max(prev - 1, 0));
      setTimeout(() => {
        setIsTransitioning(false);
      }, 300);
    }, 50);
  };

  // Calcular las imágenes visibles actualmente
  const getVisibleArticles = () => {
    return filteredTypearticles.slice(currentIndex, currentIndex + visibleItems);
  };

  if (filteredTypearticles.length === 0) {
    return null;
  }

  return (
    <div className="w-[90%] mx-auto relative px-4">
      {/* Título */}
      <div className="w-full flex items-center justify-center my-8">
        <div className="text-center">
          <h2 className="text-3xl text-slate-600 font-bold">TIPOS DE ARTÍCULOS</h2>
          <div style={{ ...getBackground("background") }} className="w-[100px] h-[2px] mt-4 mx-auto"></div>
        </div>
      </div>

      {/* Desktop & Tablet: 4 círculos con hover creciente */}
      {!isMobile && (
        <div className="relative" ref={containerRef}>
          <button
            onClick={prevSlide}
            disabled={currentIndex <= 0}
            className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-6 z-20 rounded-full w-12 h-12 flex items-center justify-center transition-all duration-300 border shadow-xl ${
              currentIndex <= 0 ? "opacity-50 cursor-not-allowed bg-gray-100 border-gray-300" : "bg-white hover:scale-110 border-gray-200"
            }`}
            aria-label="Imagen anterior">
            <svg className={`w-7 h-7 ${currentIndex <= 0 ? "text-gray-400" : "text-gray-700"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <div className="flex justify-center items-center gap-6 py-4">
            {getVisibleArticles().map((c, index) => (
              <Link
                key={`${c._id || c.name}-${currentIndex + index}`}
                to={`/products/search?typearticle=${c.name}`}
                className={`flex-shrink-0 transition-all duration-500 ease-in-out ${
                  isTransitioning ? "opacity-0 scale-95" : "opacity-100 scale-100"
                } ${isTablet ? "w-[22%]" : "w-[22%]"} group`}>
                <div className="flex flex-col items-center gap-3">
                  {/* Círculo */}
                  <div className="
                    w-68 h-68 rounded-full overflow-hidden
                    ring-2 ring-gray-200
                    transition-all duration-300 ease-out
                    group-hover:scale-110
                    group-hover:ring-4 group-hover:ring-offset-2 group-hover:ring-gray-300
                    group-hover:shadow-xl
                  ">
                    <img
                      src={c.image}
                      alt={c.name}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      onError={e => { e.target.style.display = "none"; }}
                    />
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <button
            onClick={nextSlide}
            disabled={currentIndex >= maxIndex}
            className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-6 z-20 rounded-full w-12 h-12 flex items-center justify-center transition-all duration-300 border shadow-xl ${
              currentIndex >= maxIndex ? "opacity-50 cursor-not-allowed bg-gray-100 border-gray-300" : "bg-white hover:scale-110 border-gray-200"
            }`}
            aria-label="Siguiente imagen">
            <svg className={`w-7 h-7 ${currentIndex >= maxIndex ? "text-gray-400" : "text-gray-700"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}

      {/* Mobile: grid 1 columna hacia abajo */}
      {isMobile && (
        <div className="flex flex-col items-center gap-6 py-4">
          {filteredTypearticles.map((c, index) => (
            <Link
              key={`mobile-${c._id || c.name}-${index}`}
              to={`/products/search?typearticle=${c.name}`}
              className="group outline-none">
              <div className="
                w-80 h-80
                rounded-full overflow-hidden
                ring-2 ring-gray-200
                transition-all duration-300 ease-out
                group-hover:scale-110
                group-hover:ring-4 group-hover:ring-offset-2 group-hover:ring-gray-300
                group-hover:shadow-xl
              ">
                <img
                  src={c.image}
                  alt={c.name}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  onError={e => { e.target.style.display = "none"; }}
                />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default Typearticles;
